import type { CanvasSectionBlock } from '../../sectionRegistry';
import {
  getRuntimeBlockPolicy,
  type BlockPolicy,
} from './runtime';

const BUILDER_ANCHOR_FLAG = '__builder_anchor';

type ResolveBlockPolicyOptions = {
  editorOverrides?: Record<string, Partial<BlockPolicy>>;
};

const KNOWN_BLOCK_TYPES = new Set([
  'text',
  'image',
  'decorative-asset',
  'button',
  'video',
  'social',
  'site-progress',
  'site-footer',
  'gallery',
  'request-form',
  'product-card',
  'model-viewer',
  'product-deconstructor',
  'customiser-viewer',
  'customiser-finish-picker',
  'customiser-colour-filter',
  'customiser-combination-results',
  'spacer',
]);
const warnedKeys = new Set<string>();

function devWarnOnce(key: string, message: string, context?: unknown) {
  if (!import.meta.env.DEV) return;
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn(message, context);
}

function resolvePolicyType(block: Pick<CanvasSectionBlock, 'type' | 'style'>): string {
  const style =
    block.style && typeof block.style === 'object'
      ? (block.style as Record<string, unknown>)
      : null;

  if (style?.__layout_locked === true) return 'protected-locked';

  if (style?.__protected_component === true) {
    if (style?.__template_slot === 'customiser' && typeof style?.role === 'string') {
      return 'protected-customiser-region';
    }
    return 'protected';
  }

  if (
    block.type === 'text' ||
    block.type === 'image' ||
    block.type === 'decorative-asset' ||
    block.type === 'button' ||
    block.type === 'gallery' ||
    block.type === 'site-progress' ||
    block.type === 'site-footer' ||
    block.type === 'request-form' ||
    block.type === 'product-card' ||
    block.type === 'model-viewer' ||
    block.type === 'product-deconstructor' ||
    block.type === 'customiser-viewer' ||
    block.type === 'customiser-finish-picker' ||
    block.type === 'customiser-colour-filter' ||
    block.type === 'customiser-combination-results'
  ) {
    return block.type;
  }

  return 'default';
}

export function resolveBlockPolicy(
  block: Pick<CanvasSectionBlock, 'type' | 'style'>,
  options?: ResolveBlockPolicyOptions
): BlockPolicy {
  const policyType = resolvePolicyType(block);
  const runtimePolicy = getRuntimeBlockPolicy(policyType);
  const editorOverride = options?.editorOverrides?.[policyType];
  const style =
    block.style && typeof block.style === 'object'
      ? (block.style as Record<string, unknown>)
      : null;

  const merged: BlockPolicy = {
    ...runtimePolicy,
    ...(editorOverride ?? {}),
    type: runtimePolicy.type,
  };

  if (style?.[BUILDER_ANCHOR_FLAG] === true) {
    merged.canDelete = false;
    merged.canDuplicate = false;
  }

  if (policyType === 'default' && !KNOWN_BLOCK_TYPES.has(String(block.type ?? ''))) {
    devWarnOnce(
      `unknown-block-policy:${String(block.type ?? 'undefined')}`,
      `[BlockPolicy] Unknown block type "${String(
        block.type ?? 'undefined'
      )}" resolved to default policy. Register it in runtime/editor policies.`,
      block
    );
  }

  if (
    merged.sizingMode === 'fixed-protected' &&
    style?.__protected_component !== true &&
    style?.__layout_locked !== true
  ) {
    devWarnOnce(
      `protected-metadata-missing:${String(block.type ?? 'unknown')}:${String(style?.role ?? 'no-role')}`,
      '[BlockPolicy] Fixed/protected block is missing expected protected metadata (__protected_component or __layout_locked).',
      block
    );
  }

  return merged;
}

export function isBuilderAnchorBlock(
  block: Pick<CanvasSectionBlock, 'style'> | null | undefined
): boolean {
  const style =
    block?.style && typeof block.style === 'object'
      ? (block.style as Record<string, unknown>)
      : null;
  return style?.[BUILDER_ANCHOR_FLAG] === true;
}
